import * as React from "react"

import { cn } from "@/lib/utils"

// Locally-controlled textarea: keeps its own value in state so typing is
// instant even inside very large parent components, and only propagates the
// change to the parent (debounced + on blur). This mirrors the optimized
// Input component and prevents per-keystroke re-renders of huge forms/grids.
const Textarea = React.forwardRef(({ className, value, onChange, onBlur, noDebounce, ...props }, ref) => {
  const [localVal, setLocalVal] = React.useState(value ?? '');
  const onChangeRef = React.useRef(onChange);
  const timerRef = React.useRef(null);
  onChangeRef.current = onChange;

  React.useEffect(() => {
    setLocalVal(value ?? '');
  }, [value]);

  React.useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  const handleChange = React.useCallback((e) => {
    const val = e.target.value;
    const name = e.target.name;
    setLocalVal(val);
    if (noDebounce) {
      if (onChangeRef.current) onChangeRef.current(e);
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (onChangeRef.current) onChangeRef.current({ target: { value: val, name } });
    }, 150);
  }, [noDebounce]);

  const handleBlur = React.useCallback((e) => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (onChangeRef.current && localVal !== (value ?? '')) {
      onChangeRef.current({ target: { value: localVal, name: e.target.name } });
    }
    if (onBlur) onBlur(e);
  }, [localVal, value, onBlur]);

  return (
    <textarea
      value={localVal}
      onChange={handleChange}
      onBlur={handleBlur}
      className={cn(
        "flex min-h-[80px] w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/20 focus-visible:border-orange-500 disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      ref={ref}
      {...props}
    />
  )
})
Textarea.displayName = "Textarea"

export { Textarea }
